// components/tabs/TemplateTab.jsx
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useDesignSystem, cn } from '../../designSystem';
import { Plus, Edit, Trash, Save } from "lucide-react";

const TemplateTab = ({
  searchQuery,
  setSearchQuery,
  templates,
  setEditingTemplate,
  handleDeleteTemplate,
  setChosenTemplate,
  setActiveTab,
  isMobileView,
  setIsDialogOpen, // We only use this now for dialog control
  isTabletView,
  effects
}) => {
  const { colors, typography, componentStyles } = useDesignSystem();

  // Function to open dialog for adding a new template
  const handleAddTemplate = () => {
    setEditingTemplate(null);
    setIsDialogOpen(true);
  };

  // Function to open dialog for editing a template
  const handleEditTemplate = (title, content, e) => {
    if (e) e.stopPropagation();
    setEditingTemplate({ title, content });
    setIsDialogOpen(true);
  };

  // Filter templates based on search query
  const filteredTemplates = Object.entries(templates).filter(([title, content]) => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    
    // Make sure content is a string before using toLowerCase()
    const contentString = typeof content === 'string' ? content : '';
    
    return title.toLowerCase().includes(searchLower) || 
           contentString.toLowerCase().includes(searchLower);
  });

  // Mobile version of the template tab
  if (isMobileView) {
    return (
      <div className={cn(
        effects?.glassmorphism || "",
        "rounded-lg border",
        colors.neutral.border
      )}>
        <div className="flex gap-2 mx-3 mt-2 mb-2">
          <Input
            type="text"
            placeholder="Søk i maler..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={cn(
              "flex-1",
              colors.neutral.bg,
              "dark:bg-neutral-800 border",
              colors.neutral.border,
              "text-sm h-8"
            )}
          />
          <Button
            onClick={handleAddTemplate}
            size="sm"
            className={cn(
              componentStyles.button.base,
              componentStyles.button.primary,
              "shrink-0 py-1 px-2 h-8"
            )}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="overflow-y-auto h-[calc(100vh-225px)] px-3 pb-3 space-y-2">
          {filteredTemplates.length > 0 ? (
            filteredTemplates.map(([title, content]) => (
              <div 
                key={title} 
                className={cn(
                  colors.neutral.bg,
                  "dark:bg-neutral-800 border",
                  colors.neutral.border,
                  "rounded-md p-3 shadow-sm"
                )}
                onClick={() => {
                  setChosenTemplate(title);
                  setActiveTab('notat');
                }}
              >
                <div className="flex items-center justify-between mb-1">
                  <h3 className={cn(
                    typography.cardTitle,
                    "truncate text-sm"
                  )}>
                    {title}
                  </h3>
                  <div className="flex space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleEditTemplate(title, content, e)}
                      className={cn(
                        "h-6 w-6 p-0 flex items-center justify-center",
                        colors.neutral.textTertiary,
                        "hover:text-blue-500 dark:hover:text-blue-400"
                      )}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Er du sikker på at du vil slette malen "${title}"?`)) {
                          handleDeleteTemplate(title, true);
                        }
                      }}
                      className={cn(
                        "h-6 w-6 p-0 flex items-center justify-center",
                        colors.neutral.textTertiary,
                        "hover:text-red-500 dark:hover:text-red-400"
                      )}
                    >
                      <Trash className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <p className={cn(
                  typography.small,
                  colors.neutral.textTertiary,
                  "line-clamp-2"
                )}>
                  {typeof content === 'string' ? (
                    content.substring(0, 100) + (content.length > 100 ? "..." : "")
                  ) : ""}
                </p>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center h-full p-6 text-center">
              <Save className="h-10 w-10 mb-3 opacity-20" />
              <h4 className={cn(typography.h4, "mb-2 text-sm")}>
                {searchQuery ? "Ingen maler funnet" : "Ingen maler lagt til ennå"}
              </h4>
              <p className={cn(typography.small, "mb-3 max-w-md text-xs", colors.neutral.textSecondary)}>
                {searchQuery 
                  ? `Ingen maler samsvarer med søket "${searchQuery}".` 
                  : "Maler hjelper deg å generere strukturerte notater raskere."}
              </p>
              {searchQuery ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSearchQuery("")}
                  className="mt-1"
                >
                  Tilbakestill søk
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={handleAddTemplate}
                  className={cn(componentStyles.button.primary)}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  Legg til mal
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop version of the template tab
  return (
    <Card className={cn(
      "h-[calc(100vh-220px)]",
      componentStyles.card.glass,
      "overflow-hidden flex flex-col"
    )}>
      <div className="p-6">
        <div className="flex items-center justify-end space-x-2">
          <div className="flex-1">
            <Input 
              type="text" 
              placeholder="Søk i maler..." 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
              className="w-full" 
            />
          </div>
          <Button 
            onClick={handleAddTemplate}
            className={cn(
              componentStyles.button.base,
              componentStyles.button.primary
            )}
          >
            <Plus className="mr-2 h-4 w-4" />Legg til mal
          </Button>
        </div>
      </div>
      <CardContent className="flex-1 overflow-hidden flex flex-col p-6 pt-0">
        <ScrollArea className="flex-1">
          {filteredTemplates.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pr-4 pb-4">
              {filteredTemplates.map(([title, content]) => (
                <Card
                  key={title}
                  className={cn(
                    "transition-all duration-200",
                    "border hover:border-blue-300 dark:hover:border-neutral-400",
                    "dark:bg-neutral-800 shadow-sm hover:shadow-md"
                  )}
                >
                  <div className="flex flex-col h-full">
                    <div className="p-4">
                      <div className="flex justify-between items-center">
                        <h4 className={cn(typography.h4, "truncate max-w-[70%]")}>
                          {title}
                        </h4>
                        <div className="flex items-center space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleEditTemplate(title, content, e)}
                            className="hover:text-blue-500 dark:hover:text-neutral-300 h-8 w-8 p-0"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (window.confirm(`Er du sikker på at du vil slette malen "${title}"?`)) {
                                handleDeleteTemplate(title, true);
                              }
                            }}
                            className="hover:text-red-500 dark:hover:text-red-400 h-8 w-8 p-0"
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="px-4 pb-4 flex-1">
                      <ScrollArea className="h-[120px]">
                        <div className={cn(
                          typography.body, 
                          "whitespace-pre-wrap pr-4",
                          colors.neutral.textPrimary
                        )}>
                          {typeof content === 'string' ? content : ''}
                        </div>
                      </ScrollArea>
                    </div>
                    
                    <div className="px-4 py-2 text-right">
                      <Button
                        variant="link"
                        onClick={() => {
                          setChosenTemplate(title);
                          setActiveTab('notat');
                        }}
                        className={cn(
                          "p-0 h-auto",
                          colors.primary.text,
                          "hover:underline dark:hover:text-neutral-300"
                        )}
                      >
                        Bruk denne malen
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center p-8 text-center">
              <Save className="h-12 w-12 mb-4 opacity-20" />
              <h4 className={cn(typography.h4, "mb-2")}>
                {searchQuery ? "Ingen maler funnet" : "Ingen maler lagt til ennå"}
              </h4>
              <p className={cn(typography.body, "mb-4 max-w-md", colors.neutral.textSecondary)}>
                {searchQuery 
                  ? `Ingen maler samsvarer med søket "${searchQuery}".` 
                  : "Du har ikke lagt til noen maler ennå. Maler hjelper deg å generere strukturerte notater raskere."}
              </p>
              {searchQuery ? (
                <Button
                  variant="outline"
                  onClick={() => setSearchQuery("")}
                  className="mt-2"
                >
                  Tilbakestill søk
                </Button>
              ) : (
                <Button
                  onClick={handleAddTemplate}
                  className={cn(componentStyles.button.primary)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Legg til din første mal
                </Button>
              )}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default TemplateTab;